/**
 * InsightPro™ — Store Performance v2
 *
 * Operational performance grid for Pedros-style multi-entity tenants. Built on
 * the same getEntityPLSummary endpoint as Group P&L but adds:
 *   - KPI hero strip (count of profitable stores, critical, total Sales, etc.)
 *   - Top/bottom mover callout cards
 *   - Group-by toggle (Country / Province / Brand / Type / Entity) with chip
 *     filters that stack
 *   - 3-month sales sparkline per row (entity view only)
 *   - RAG cell colouring on GP% / EBITDA% / Δ
 *   - Click row → GL line drilldown (existing endpoint)
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  fetchFinancePeriods, fetchFinanceBrands,
  fetchEntityPLSummary, fetchEntityPLDetail,
} from '../utils/api';

// ── Format helpers ───────────────────────────────────────────
const fmtMoney = (v) => {
  if (v == null || isNaN(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return (v / 1_000_000).toFixed(2) + 'M';
  if (abs >= 1_000)     return (v / 1_000).toFixed(0) + 'K';
  return v.toFixed(0);
};
const fmtPct   = (v) => (v == null || isNaN(v)) ? '—' : `${(v * 100).toFixed(1)}%`;
const fmtDelta = (v) => v == null || isNaN(v) ? '—' :
  (v >= 0 ? '+' : '') + (v * 100).toFixed(1) + '%';

// RAG thresholds (per-tenant settings later)
const RAG = {
  gp:     { red: 0.25, amber: 0.30 },
  ebitda: { red: 0,    amber: 0.05 },
  delta:  { red: 0,    amber: 0.10 },
};
const ragColor = (v, t) => {
  if (v == null || isNaN(v)) return 'var(--page-muted)';
  if (v < t.red)   return 'var(--red)';
  if (v < t.amber) return 'var(--amber)';
  return 'var(--green-l)';
};

// Group-by config (mirrors Group P&L)
const GROUP_BY_OPTIONS = [
  { key: 'entity',      label: 'Entity',   filterKey: null,         rowLabel: r => r.legalName || '—' },
  { key: 'province',    label: 'Province', filterKey: 'province',   rowLabel: r => r.province || (r.country !== 'ZA' ? `(${r.country})` : '—') },
  { key: 'country',     label: 'Country',  filterKey: 'country',    rowLabel: r => r.country  || '—' },
  { key: 'brand',       label: 'Brand',    filterKey: 'brand',      rowLabel: r => r.brand    || '—' },
  { key: 'entity_type', label: 'Type',     filterKey: 'entityType', rowLabel: r => r.entityType || '—' },
];
const GROUP_BY_BY_KEY = Object.fromEntries(GROUP_BY_OPTIONS.map(o => [o.key, o]));

// ── Styles ───────────────────────────────────────────────────
const S = {
  page:        { padding: '20px 28px', maxWidth: 1700 },
  header:      { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 16 },
  title:       { fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--page-text)', letterSpacing: '-0.02em' },
  sub:         { fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)', marginTop: 4, letterSpacing: '0.1em', textTransform: 'uppercase' },
  selectorBar: { display: 'flex', gap: 12, alignItems: 'flex-end' },
  label:       { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 5 },
  select:      { background: 'var(--page)', border: '1px solid var(--card-border)', padding: '8px 12px', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--page-text)', outline: 'none', borderRadius: 2, minWidth: 140 },
  kpiGrid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 16 },
  kpiCard:     { background: 'var(--card)', border: '1px solid var(--card-border)', borderRadius: 4, padding: '14px 18px' },
  kpiLabel:    { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 6 },
  kpiVal:      { fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--page-text)', lineHeight: 1 },
  kpiSub:      { fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)', marginTop: 6 },
  moverGrid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 16 },
  moverCard:   { background: 'var(--card)', border: '1px solid var(--card-border)', borderRadius: 4, padding: '14px 18px', cursor: 'pointer', transition: 'border-color 0.15s' },
  moverTitle:  { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 6 },
  moverEntity: { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--page-text)', fontWeight: 600, marginBottom: 4, lineHeight: 1.3 },
  moverMetric: { fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, lineHeight: 1 },
  moverSub:    { fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)', marginTop: 4 },
  card:        { background: 'var(--card)', border: '1px solid var(--card-border)', borderRadius: 4, padding: '16px 20px', marginBottom: 16 },
  cardTitle:   { fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 12 },
  table:       { width: '100%', borderCollapse: 'collapse' },
  th:          { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--page-muted)', padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid var(--card-border)', background: 'var(--page)', cursor: 'pointer', userSelect: 'none' },
  thRight:     { textAlign: 'right' },
  td:          { padding: '8px 10px', fontSize: 12.5, color: 'var(--page-text)', borderBottom: '1px solid var(--card-border)', verticalAlign: 'middle' },
  tdRight:     { textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12 },
  tdMono:      { fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)' },
  rowClick:    { cursor: 'pointer' },
  errorBox:    { padding: 14, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--red)', background: 'rgba(176,40,40,0.08)', border: '1px solid rgba(176,40,40,0.2)', borderRadius: 2, marginBottom: 16 },
  loadingBox:  { padding: 28, textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)' },
  brandPill:   (brand) => ({
    fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase',
    padding: '2px 8px', borderRadius: 2, display: 'inline-block',
    background: brand === 'Pedros' ? 'rgba(37,99,235,0.10)' : brand === 'Wackys' ? 'rgba(200,149,32,0.10)' : 'rgba(26,173,99,0.10)',
    color:      brand === 'Pedros' ? '#2563eb'              : brand === 'Wackys' ? 'var(--amber)'           : 'var(--green-l)',
  }),
  closeBtn:    { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'transparent', border: '1px solid var(--card-border)', padding: '4px 10px', cursor: 'pointer', color: 'var(--page-muted)', borderRadius: 2 },
  filterChip:  { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: 'rgba(200,149,32,0.10)', border: '1px solid rgba(200,149,32,0.25)', borderRadius: 2, fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--amber)' },
};

// ── Inline SVG sparkline ─────────────────────────────────────
function Sparkline({ data, width = 70, height = 22 }) {
  if (!data || data.length < 2) return <span style={{ color: 'var(--page-muted)' }}>—</span>;
  const ys = data.map(d => d.sales);
  const min = Math.min(...ys);
  const max = Math.max(...ys);
  const range = max - min || 1;
  const stepX = width / (data.length - 1);
  const points = data.map((d, i) => {
    const x = i * stepX;
    const y = height - ((d.sales - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  // Trend colour: last point vs first
  const trendUp = ys[ys.length - 1] >= ys[0];
  const stroke = trendUp ? 'var(--green-l)' : 'var(--red)';
  return (
    <svg width={width} height={height} style={{ verticalAlign: 'middle', display: 'inline-block' }}>
      <polyline points={points} fill="none" stroke={stroke} strokeWidth="1.5" />
      <circle cx={(data.length - 1) * stepX} cy={height - ((ys[ys.length - 1] - min) / range) * (height - 4) - 2}
              r="2" fill={stroke} />
    </svg>
  );
}

// ── Page ─────────────────────────────────────────────────────
export default function StorePerformance() {
  const [periods, setPeriods]   = useState([]);
  const [period,  setPeriod]    = useState(null);
  const [brands,  setBrands]    = useState({ brands: ['Consolidated'], canPickBrand: false });
  const [brand,   setBrand]     = useState('Consolidated');
  const [data,    setData]      = useState(null);
  const [loading, setLoading]   = useState(true);
  const [err,     setErr]       = useState(null);
  const [groupBy, setGroupBy]   = useState('entity');
  const [filters, setFilters]   = useState({});
  const [sort,    setSort]      = useState({ col: 'sales', dir: 'desc' });
  const [drillEntity, setDrillEntity] = useState(null);
  const [drillData,   setDrillData]   = useState(null);

  // Periods + brands once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, b] = await Promise.all([fetchFinancePeriods(), fetchFinanceBrands()]);
        if (cancelled) return;
        setPeriods(p.periods || []);
        setPeriod(p.latest || (p.periods?.[0]?.periodKey ?? null));
        setBrands(b);
        if (b.canPickBrand) setBrand('Consolidated');
        else setBrand(b.brands[0] || 'Consolidated');
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.error || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Reload on period / brand / groupBy / filters change
  useEffect(() => {
    if (!period) return;
    let cancelled = false;
    setLoading(true); setErr(null); setDrillEntity(null); setDrillData(null);
    const apiBrand = brand === 'Consolidated' ? 'all' : brand;
    fetchEntityPLSummary(period, apiBrand, {
      groupBy,
      ...filters,
      comparePeriod: 'priorMonth',
      trendMonths: groupBy === 'entity' ? 3 : 0,
    })
      .then(r => { if (!cancelled) { setData(r); setLoading(false); } })
      .catch(e => { if (!cancelled) { setErr(e.response?.data?.error || e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [period, brand, groupBy, filters]);

  const periodOptions = useMemo(() =>
    [...periods].sort((a, b) => (b.periodKey || '').localeCompare(a.periodKey || '')),
    [periods]
  );

  const rows = data?.entities || [];

  // KPIs (entity level only — for non-entity, hero collapses to totals)
  const kpis = useMemo(() => {
    if (!rows.length) return null;
    const totalSales = rows.reduce((s, r) => s + (r.sales || 0), 0);
    const totalSalesPrev = rows.reduce((s, r) => s + (r.salesPrev || 0), 0);
    const totalEbitda = rows.reduce((s, r) => s + (r.ebitda || 0), 0);
    const totalEbitdaPrev = rows.reduce((s, r) => s + (r.ebitdaPrev || 0), 0);
    const tracked    = rows.length;
    const profitable = rows.filter(r => r.ebitda > 0).length;
    const critical   = rows.filter(r => r.ebitdaPct != null && r.ebitdaPct < RAG.ebitda.red).length;
    const ebitdaPcts = rows.map(r => r.ebitdaPct).filter(p => p != null).sort((a, b) => a - b);
    const median     = ebitdaPcts.length ? ebitdaPcts[Math.floor(ebitdaPcts.length / 2)] : null;
    return {
      tracked,
      profitable, profitablePct: tracked ? profitable / tracked : null,
      critical,   criticalPct:   tracked ? critical   / tracked : null,
      totalSales, salesDeltaPct: totalSalesPrev ? (totalSales - totalSalesPrev) / Math.abs(totalSalesPrev) : null,
      totalEbitda, ebitdaDeltaPct: totalEbitdaPrev ? (totalEbitda - totalEbitdaPrev) / Math.abs(totalEbitdaPrev) : null,
      medianEbitdaPct: median,
    };
  }, [rows]);

  // Top / bottom mover cards (entity level only)
  const movers = useMemo(() => {
    if (groupBy !== 'entity' || rows.length < 4) return null;
    const withEbitdaPct = rows.filter(r => r.ebitdaPct != null);
    const withSalesDelta = rows.filter(r => r.salesDeltaPct != null && r.salesPrev > 0);
    return {
      topEbitda:    [...withEbitdaPct].sort((a, b) => b.ebitdaPct - a.ebitdaPct)[0],
      worstEbitda:  [...withEbitdaPct].sort((a, b) => a.ebitdaPct - b.ebitdaPct)[0],
      topMover:     [...withSalesDelta].sort((a, b) => b.salesDeltaPct - a.salesDeltaPct)[0],
      worstMover:   [...withSalesDelta].sort((a, b) => a.salesDeltaPct - b.salesDeltaPct)[0],
    };
  }, [rows, groupBy]);

  // Sort
  const sortedRows = useMemo(() => {
    const out = rows.slice();
    const { col, dir } = sort;
    const mul = dir === 'asc' ? 1 : -1;
    out.sort((a, b) => {
      const av = a[col]; const bv = b[col];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * mul;
      return String(av).localeCompare(String(bv)) * mul;
    });
    return out;
  }, [rows, sort]);

  const toggleSort = (col) => setSort(s => s.col === col ? { col, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { col, dir: 'desc' });
  const sortIndicator = (col) => sort.col !== col ? '' : sort.dir === 'desc' ? ' ↓' : ' ↑';
  const removeFilter = (k) => setFilters(f => { const out = { ...f }; delete out[k]; return out; });
  const clearFilters = () => { setFilters({}); setGroupBy('entity'); };

  // Row click: drill to entity (for non-entity groupBy) or open GL detail (entity).
  const handleRowClick = async (row) => {
    if (groupBy === 'entity') {
      setDrillEntity(row);
      setDrillData(null);
      try {
        const d = await fetchEntityPLDetail(period, row.entityKey);
        setDrillData(d);
      } catch (e) {
        setDrillData({ error: e.response?.data?.error || e.message });
      }
      return;
    }
    if (groupBy === 'brand') {
      if (row.brand) setBrand(row.brand);
      setGroupBy('entity');
      return;
    }
    const opt = GROUP_BY_BY_KEY[groupBy];
    if (!opt?.filterKey) return;
    const v = row[opt.filterKey];
    if (!v) return;
    setFilters(f => ({ ...f, [opt.filterKey]: v }));
    setGroupBy('entity');
  };

  const moverClick = (mover) => mover && handleRowClick(mover);

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Store Performance</div>
          <div style={S.sub}>
            {brand === 'Consolidated' ? 'Consolidated · all brands' : `${brand} only`}
            {' · '}{period || '—'}
            {data?.priorPeriodKey && ` (vs ${data.priorPeriodKey})`}
            {filters.country     && ` · ${filters.country}`}
            {filters.province    && ` · ${filters.province}`}
            {filters.entityType  && ` · ${filters.entityType}`}
          </div>
        </div>
        <div style={S.selectorBar}>
          {brands.canPickBrand && (
            <div>
              <div style={S.label}>Brand</div>
              <select style={S.select} value={brand} onChange={e => setBrand(e.target.value)}>
                {brands.brands.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          )}
          <div>
            <div style={S.label}>Period</div>
            <select style={S.select} value={period || ''} onChange={e => setPeriod(e.target.value)}>
              {periodOptions.map(p => (
                <option key={p.periodKey} value={p.periodKey}>{p.periodKey}{p.monthName ? ` · ${p.monthName}` : ''}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {err && <div style={S.errorBox}>⚠ {err}</div>}
      {loading && <div style={S.loadingBox}>Loading…</div>}

      {!loading && data && (
        <>
          {/* KPI hero strip */}
          {kpis && (
            <div style={S.kpiGrid}>
              <KpiTile label="Entities tracked" value={kpis.tracked.toString()}
                       sub={groupBy === 'entity' ? 'across this filter' : `grouped by ${GROUP_BY_BY_KEY[groupBy]?.label}`} />
              <KpiTile label="Profitable" value={kpis.profitable.toString()}
                       sub={fmtPct(kpis.profitablePct)} valueColor="var(--green-l)" />
              <KpiTile label="Critical (EBITDA<0)" value={kpis.critical.toString()}
                       sub={fmtPct(kpis.criticalPct)} valueColor={kpis.critical ? 'var(--red)' : 'var(--page-muted)'} />
              <KpiTile label="Total Sales" value={fmtMoney(kpis.totalSales)}
                       sub={kpis.salesDeltaPct != null ? `${fmtDelta(kpis.salesDeltaPct)} MoM` : ''}
                       valueColor={ragColor(kpis.salesDeltaPct, RAG.delta)} />
              <KpiTile label="Total EBITDA" value={fmtMoney(kpis.totalEbitda)}
                       sub={kpis.ebitdaDeltaPct != null ? `${fmtDelta(kpis.ebitdaDeltaPct)} MoM` : ''}
                       valueColor={ragColor(kpis.totalEbitda > 0 ? RAG.ebitda.amber : -1, RAG.ebitda)} />
              <KpiTile label="Median EBITDA%" value={fmtPct(kpis.medianEbitdaPct)}
                       sub="across rows" valueColor={ragColor(kpis.medianEbitdaPct, RAG.ebitda)} />
            </div>
          )}

          {/* Top / bottom mover cards */}
          {movers && (
            <div style={S.moverGrid}>
              <MoverCard title="🏆 Top EBITDA%" entity={movers.topEbitda} metric={fmtPct(movers.topEbitda?.ebitdaPct)}
                         sub={fmtMoney(movers.topEbitda?.ebitda) + ' EBITDA'} color="var(--green-l)" onClick={() => moverClick(movers.topEbitda)} />
              <MoverCard title="🚨 Worst EBITDA%" entity={movers.worstEbitda} metric={fmtPct(movers.worstEbitda?.ebitdaPct)}
                         sub={fmtMoney(movers.worstEbitda?.ebitda) + ' EBITDA'} color="var(--red)" onClick={() => moverClick(movers.worstEbitda)} />
              <MoverCard title="📈 Biggest Sales Gain" entity={movers.topMover} metric={fmtDelta(movers.topMover?.salesDeltaPct)}
                         sub={fmtMoney(movers.topMover?.sales) + ' Sales'} color="var(--green-l)" onClick={() => moverClick(movers.topMover)} />
              <MoverCard title="📉 Biggest Sales Drop" entity={movers.worstMover} metric={fmtDelta(movers.worstMover?.salesDeltaPct)}
                         sub={fmtMoney(movers.worstMover?.sales) + ' Sales'} color="var(--red)" onClick={() => moverClick(movers.worstMover)} />
            </div>
          )}

          {/* Group-by toggle + filter chips */}
          <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:12, fontSize:12 }}>
            <div style={{ ...S.label, marginBottom:0 }}>Group by</div>
            <div style={{ display:'flex', gap:4, border:'1px solid var(--card-border)', borderRadius:2 }}>
              {GROUP_BY_OPTIONS.map(o => (
                <button key={o.key} onClick={() => setGroupBy(o.key)}
                        style={{
                          fontFamily:'var(--font-mono)', fontSize:10, letterSpacing:'0.1em',
                          textTransform:'uppercase', padding:'7px 14px', cursor:'pointer',
                          background: groupBy === o.key ? 'var(--gold)' : 'transparent',
                          color:      groupBy === o.key ? 'var(--void)' : 'var(--page-muted)',
                          border:'none',
                        }}>{o.label}</button>
              ))}
            </div>
            {Object.entries(filters).filter(([,v]) => v).map(([k, v]) => (
              <span key={k} style={S.filterChip}>
                {k}: {v}
                <button onClick={() => removeFilter(k)} style={{ background:'transparent', border:'none', color:'var(--amber)', cursor:'pointer', padding:0, fontSize:13, lineHeight:1 }}>×</button>
              </span>
            ))}
            {Object.values(filters).some(Boolean) && (
              <button onClick={clearFilters} style={{ background:'transparent', border:'none', color:'var(--page-muted)', cursor:'pointer', fontFamily:'var(--font-mono)', fontSize:10, letterSpacing:'0.1em', textTransform:'uppercase' }}>Clear all</button>
            )}
          </div>

          {/* Detail table */}
          <div style={S.card}>
            <div style={S.cardTitle}>
              {groupBy === 'entity' ? 'Entities — RAG by EBITDA%, click row for GL detail' : `By ${GROUP_BY_BY_KEY[groupBy]?.label} — click a row to drill into entities`}
            </div>
            {sortedRows.length === 0 ? (
              <div style={S.loadingBox}>No data for this filter combination.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th} onClick={() => toggleSort(groupBy === 'entity' ? 'legalName' : 'country')}>{GROUP_BY_BY_KEY[groupBy]?.label || 'Group'}</th>
                      {groupBy === 'entity' && <th style={S.th} onClick={() => toggleSort('brand')}>Brand{sortIndicator('brand')}</th>}
                      {groupBy === 'entity' && <th style={S.th} onClick={() => toggleSort('entityType')}>Type{sortIndicator('entityType')}</th>}
                      {groupBy === 'entity' && <th style={S.th} onClick={() => toggleSort('province')}>Province{sortIndicator('province')}</th>}
                      <th style={{...S.th, ...S.thRight}} onClick={() => toggleSort('sales')}>Sales{sortIndicator('sales')}</th>
                      <th style={{...S.th, ...S.thRight}} onClick={() => toggleSort('salesDeltaPct')}>Δ Sales{sortIndicator('salesDeltaPct')}</th>
                      {groupBy === 'entity' && <th style={S.th}>3-mo Trend</th>}
                      <th style={{...S.th, ...S.thRight}} onClick={() => toggleSort('grossProfit')}>GP{sortIndicator('grossProfit')}</th>
                      <th style={{...S.th, ...S.thRight}} onClick={() => toggleSort('grossProfitPct')}>GP%{sortIndicator('grossProfitPct')}</th>
                      <th style={{...S.th, ...S.thRight}} onClick={() => toggleSort('otherExpense')}>Op. Exp.{sortIndicator('otherExpense')}</th>
                      <th style={{...S.th, ...S.thRight}} onClick={() => toggleSort('ebitda')}>EBITDA{sortIndicator('ebitda')}</th>
                      <th style={{...S.th, ...S.thRight}} onClick={() => toggleSort('ebitdaPct')}>EBITDA%{sortIndicator('ebitdaPct')}</th>
                      <th style={{...S.th, ...S.thRight}} onClick={() => toggleSort('ebitdaDeltaPct')}>Δ EBITDA{sortIndicator('ebitdaDeltaPct')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((e, i) => {
                      const opt = GROUP_BY_BY_KEY[groupBy];
                      const labelFn = opt?.rowLabel || (r => r.legalName || '—');
                      const rowKey = e.entityKey || `${e.country || ''}|${e.province || ''}|${e.brand || ''}|${e.entityType || ''}|${i}`;
                      const gpColor = ragColor(e.grossProfitPct, RAG.gp);
                      const ebColor = ragColor(e.ebitdaPct, RAG.ebitda);
                      const sdColor = ragColor(e.salesDeltaPct, RAG.delta);
                      const edColor = ragColor(e.ebitdaDeltaPct, RAG.delta);
                      return (
                        <tr key={rowKey} style={S.rowClick} onClick={() => handleRowClick(e)}>
                          <td style={S.td}>{labelFn(e)}</td>
                          {groupBy === 'entity' && <td style={S.td}><span style={S.brandPill(e.brand)}>{e.brand}</span></td>}
                          {groupBy === 'entity' && <td style={{...S.td, ...S.tdMono}}>{e.entityType}</td>}
                          {groupBy === 'entity' && <td style={{...S.td, ...S.tdMono}}>{e.province || (e.country !== 'ZA' ? `(${e.country})` : '—')}</td>}
                          <td style={{...S.td, ...S.tdRight}}>{fmtMoney(e.sales)}</td>
                          <td style={{...S.td, ...S.tdRight, color: sdColor}}>{fmtDelta(e.salesDeltaPct)}</td>
                          {groupBy === 'entity' && <td style={S.td}><Sparkline data={e.salesTrend} /></td>}
                          <td style={{...S.td, ...S.tdRight}}>{fmtMoney(e.grossProfit)}</td>
                          <td style={{...S.td, ...S.tdRight, color: gpColor, fontWeight: 600}}>{fmtPct(e.grossProfitPct)}</td>
                          <td style={{...S.td, ...S.tdRight}}>{fmtMoney(e.otherExpense)}</td>
                          <td style={{...S.td, ...S.tdRight, color: ebColor}}>{fmtMoney(e.ebitda)}</td>
                          <td style={{...S.td, ...S.tdRight, color: ebColor, fontWeight: 600}}>{fmtPct(e.ebitdaPct)}</td>
                          <td style={{...S.td, ...S.tdRight, color: edColor}}>{fmtDelta(e.ebitdaDeltaPct)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* GL drilldown panel */}
          {drillEntity && (
            <div style={S.card}>
              <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12}}>
                <div>
                  <div style={S.cardTitle}>GL Lines — {drillEntity.legalName}</div>
                  <div style={{fontFamily:'var(--font-mono)', fontSize:11, color:'var(--page-muted)'}}>
                    {drillEntity.brand} · {drillEntity.entityType} · {drillEntity.country}{drillEntity.province ? ` / ${drillEntity.province}` : ''} · {drillEntity.currency}
                  </div>
                </div>
                <button style={S.closeBtn} onClick={() => { setDrillEntity(null); setDrillData(null); }}>✕ Close</button>
              </div>
              {!drillData ? (
                <div style={S.loadingBox}>Loading lines…</div>
              ) : drillData.error ? (
                <div style={S.errorBox}>⚠ {drillData.error}</div>
              ) : drillData.lines.length === 0 ? (
                <div style={S.loadingBox}>No GL lines for this entity / period.</div>
              ) : (
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Account Type</th>
                      <th style={S.th}>GL</th>
                      <th style={S.th}>Description</th>
                      <th style={S.th}>Category</th>
                      <th style={{...S.th, ...S.thRight}}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drillData.lines.map((l, i) => (
                      <tr key={i}>
                        <td style={{...S.td, ...S.tdMono}}>{l.accountType}</td>
                        <td style={{...S.td, ...S.tdMono}}>{l.glAccount}</td>
                        <td style={S.td}>{l.glDescription}</td>
                        <td style={{...S.td, ...S.tdMono}}>{l.reportCategory || '—'}</td>
                        <td style={{...S.td, ...S.tdRight, color: l.amount >= 0 ? 'var(--green-l)' : 'var(--red)'}}>{fmtMoney(l.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function KpiTile({ label, value, sub, valueColor }) {
  return (
    <div style={S.kpiCard}>
      <div style={S.kpiLabel}>{label}</div>
      <div style={{ ...S.kpiVal, ...(valueColor ? { color: valueColor } : {}) }}>{value}</div>
      {sub && <div style={S.kpiSub}>{sub}</div>}
    </div>
  );
}

function MoverCard({ title, entity, metric, sub, color, onClick }) {
  if (!entity) return null;
  return (
    <div style={S.moverCard} onClick={onClick}
         onMouseEnter={e => e.currentTarget.style.borderColor = color}
         onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--card-border)'}>
      <div style={S.moverTitle}>{title}</div>
      <div style={S.moverEntity}>{entity.legalName}</div>
      <div style={{ ...S.moverMetric, color }}>{metric}</div>
      <div style={S.moverSub}>{sub} · {entity.brand} · {entity.entityType}</div>
    </div>
  );
}
